import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import services from 'services/services';
import firebase from 'firebase/compat/app';
import 'firebase/compat/app-check';

// Import actions
import {
	userAuthSuccess,
	userAuthError,
	userAuthMultipleAccounts,
	clearAuthMultipleAccounts,
	userLinkAccount,
	saveProviderToken,
	deleteProviderToken,
	appCheckError
} from 'store/actions';

// Import context
import { AuthenticationContext } from 'components/context/authentication/AuthenticationProvider';

// Import helpers
import { checkAuthorizationError, getInitialValues } from 'helpers/auth';

// Import api
import {
	POST_MULTIPLE_ACCOUNT_LOGIN_API,
	POST_SUBSCRIBER_LOGIN_EXTERNAL_API
} from 'helpers/api';

const useAuthorization = ({ apiUrl, multiAccount = false, providerToken }) => {
	const dispatch = useDispatch();
	const initialValues = getInitialValues();
	const { handleLoader } = useContext(AuthenticationContext);

	const isMultipleAccountsLogin = apiUrl === POST_MULTIPLE_ACCOUNT_LOGIN_API;
	const isExternalLogin = apiUrl === POST_SUBSCRIBER_LOGIN_EXTERNAL_API;

	const handleUserAuthorization = async (values) => {
		const url = `${apiUrl}?multiAccount=${multiAccount}`;
		let dataProps = values;

		// If user logs in via external provider
		// Save token in the store
		// Check whether account is already linked
		if (isExternalLogin && values?.idToken) {
			saveProviderToken({
				idToken: values?.idToken,
				providerId: values?.providerId
			})(dispatch);
			dataProps = {};
		}

		const payload = {
			...initialValues,
			...dataProps,
			id_token: values?.idToken ?? providerToken ?? undefined
		};

		let appCheckTokenResponse;
		try {
			appCheckTokenResponse = await firebase.appCheck().getToken(false);
		} catch (error) {
			appCheckError()(dispatch);
			return;
		}

		try {
			handleLoader(true);
			const { data } = await services.post(
				url,
				payload,
				false,
				appCheckTokenResponse.token
			);
			userAuthSuccess({
				user: data,
				isExternalLogin,
				providerId: values?.providerId
			})(dispatch);
			// After successful login with provider, clear store from provider related data
			if (providerToken) deleteProviderToken()(dispatch);
		} catch (error) {
			const {
				data,
				isAuthorizationError, // General error
				isMultipleAccount, // Success but must choose account
				isExternalProviderError // Success but must link account
			} = checkAuthorizationError(error);

			if (isExternalProviderError) {
				userLinkAccount()(dispatch);
			} else if (isAuthorizationError) {
				userAuthError(error)(dispatch);
			} else if (isMultipleAccount) {
				userAuthMultipleAccounts(data)(dispatch);
			} else {
				userAuthSuccess({
					user: data,
					isExternalLogin,
					providerId: values?.providerId
				})(dispatch);
			}
		} finally {
			isMultipleAccountsLogin && clearAuthMultipleAccounts()(dispatch);
		}
	};

	return { userAuthorization: handleUserAuthorization };
};

export default useAuthorization;
